import type { BigNumberish } from 'ethers';
import { BigNumber, constants } from 'ethers';

import type { StarsToken } from '../lib/contracts';
import { StarsTokenFactory } from '../lib/contracts';
import { STARS_ADDRESS, weiToEth } from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';

export const useStarsContract = () => {
  const { contract, ...walletInfo } = useContract<StarsToken>(
    StarsTokenFactory,
    STARS_ADDRESS,
  );

  const allowanceBN = async (owner: string, spender: string) => {
    if (!contract) {
      return BigNumber.from('0');
    }

    return contract.allowance(owner, spender);
  };

  const allowance = async (owner: string, spender: string) => {
    const allowanceMantissa = await allowanceBN(owner, spender);
    return weiToEth(allowanceMantissa);
  };

  const approve = async (spender: string, amount: BigNumberish) => {
    if (!contract) {
      return false;
    }

    const trx = await contract.approve(spender, amount);
    await trx.wait();
    return true;
  };

  return {
    walletInfo,
    starsAddress: contract?.address,
    initialized: contract !== undefined,
    currentWalletAllowanceBN: async (spender: string) => {
      return allowanceBN(walletInfo.address ?? '', spender);
    },
    currentWalletAllowance: async (spender: string) => {
      return allowance(walletInfo.address ?? '', spender);
    },
    allowance,
    approveStakingContract: async (spender: string, amount: BigNumberish) => {
      if (!contract) {
        return;
      }
      try {
        const trx = await contract.approve(spender, amount);
        await trx.wait();
        return true;
      } catch (e) {
        window.Sentry?.captureException(e, {
          tags: {
            file: 'stars-contract.hooks.ts',
            function: 'approveStakingContract',
          },
        });
        return false;
      }
    },
    approveInfinite: async (spender: string) => {
      return approve(spender, constants.MaxUint256);
    },
    approve,
    getBalance: async (address: string) => {
      if (!contract) {
        return;
      }
      const balance = await contract.balanceOf(address);
      return balance;
    },
  };
};
